'use client'

import { useEffect, useRef, useState } from 'react'
import {
  Camera,
  Mesh,
  Plane,
  Program,
  Renderer,
  Texture,
  Transform,
  type OGLRenderingContext,
} from 'ogl'

type GalleryItem = { image: string; text: string }

type RockGalleryProps = {
  items: GalleryItem[]
  /** Curvature of the arc. Higher bends the row more. */
  bend?: number
  textColour?: string
  /** Canvas font shorthand for the labels. */
  font?: string
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

function labelTexture(gl: OGLRenderingContext, text: string, font: string, colour: string) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')!
  ctx.font = font
  const size = Number(font.match(/(\d+)px/)?.[1] ?? 30)
  canvas.width = Math.ceil(ctx.measureText(text).width) + 24
  canvas.height = Math.ceil(size * 1.3) + 20
  ctx.font = font
  ctx.fillStyle = colour
  ctx.textBaseline = 'middle'
  ctx.textAlign = 'center'
  ctx.fillText(text, canvas.width / 2, canvas.height / 2)
  const texture = new Texture(gl, { generateMipmaps: false })
  texture.image = canvas
  return { texture, width: canvas.width, height: canvas.height }
}

/**
 * The card edge is torn, not rounded. The site's section tears are SVG masks
 * built from feTurbulence fractal noise displacing a straight edge, so the same
 * idea runs here in the fragment shader: fbm noise pushes the rectangle's edge
 * in and out, which keeps the gallery in the same visual language as every
 * torn-top and torn-bottom on the site.
 */
const FRAGMENT = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform vec2 uImageSizes;
uniform vec2 uPlaneSizes;
uniform sampler2D tMap;
uniform float uEdge;
uniform float uSeed;
varying vec2 vUv;
varying float vNdcX;

vec2 hash(vec2 p) {
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(dot(hash(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0)),
                 dot(hash(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0)), u.x),
             mix(dot(hash(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0)),
                 dot(hash(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0)), u.x), u.y);
}

// Four octaves, same order of roughness as the numOctaves="4" on the SVG tears.
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p *= 2.0;
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 ratio = vec2(
    min((uPlaneSizes.x / uPlaneSizes.y) / (uImageSizes.x / uImageSizes.y), 1.0),
    min((uPlaneSizes.y / uPlaneSizes.x) / (uImageSizes.y / uImageSizes.x), 1.0)
  );
  vec2 uv = vec2(
    vUv.x * ratio.x + (1.0 - ratio.x) * 0.5,
    vUv.y * ratio.y + (1.0 - ratio.y) * 0.5
  );
  vec4 colour = texture2D(tMap, uv);

  // Distance to the nearest edge, then chew that boundary with noise so the
  // card looks ripped rather than cut.
  vec2 d = min(vUv, 1.0 - vUv);
  float edge = min(d.x, d.y);
  float tear = fbm(vUv * 9.0 + uSeed) * 0.5 + fbm(vUv * 26.0 + uSeed) * 0.18;
  float cut = uEdge + tear * uEdge * 1.5;
  float alpha = smoothstep(cut - 0.012, cut + 0.012, edge);

  // Cards dissolve as they reach the screen edge instead of getting cut with
  // a hard vertical line the torn edges make look broken.
  float edgeFade = 1.0 - smoothstep(0.82, 1.0, abs(vNdcX));
  alpha *= edgeFade;

  if (alpha < 0.01) discard;
  gl_FragColor = vec4(colour.rgb, colour.a * alpha);
}
`

const VERTEX = `
precision highp float;
attribute vec3 position;
attribute vec2 uv;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
uniform float uTime;
uniform float uSpeed;
varying vec2 vUv;
varying float vNdcX;
void main() {
  vUv = uv;
  vec3 p = position;
  p.z = (sin(p.x * 4.0 + uTime) * 1.5 + cos(p.y * 2.0 + uTime) * 1.5) * (0.1 + uSpeed * 0.5);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(p, 1.0);
  vNdcX = gl_Position.x / gl_Position.w;
}
`

class Card {
  plane: Mesh
  program: Program
  extra = 0
  width = 0
  widthTotal = 0
  x = 0
  scale = 1
  speed = 0
  baseScaleX = 1
  baseScaleY = 1

  constructor(
    private gl: OGLRenderingContext,
    geometry: Plane,
    scene: Transform,
    private item: GalleryItem,
    private index: number,
    private length: number,
    private screen: { width: number; height: number },
    private viewport: { width: number; height: number },
    private bend: number,
    textColour: string,
    font: string
  ) {
    // The gallery photos are 800x1000, which is not a power of two. WebGL1,
    // which is what iOS Safari gives you, cannot mipmap or repeat an NPOT
    // texture: it renders black instead. Clamped edges with a plain linear
    // filter is the combination that is safe everywhere.
    const texture = new Texture(gl, {
      generateMipmaps: false,
      wrapS: gl.CLAMP_TO_EDGE,
      wrapT: gl.CLAMP_TO_EDGE,
      minFilter: gl.LINEAR,
      magFilter: gl.LINEAR,
    })
    this.program = new Program(gl, {
      depthTest: false,
      depthWrite: false,
      vertex: VERTEX,
      fragment: FRAGMENT,
      uniforms: {
        tMap: { value: texture },
        uPlaneSizes: { value: [0, 0] },
        uImageSizes: { value: [0, 0] },
        uSpeed: { value: 0 },
        uTime: { value: 100 * (index / Math.max(1, length)) },
        uEdge: { value: 0.035 },
        uSeed: { value: index * 13.37 },
      },
      transparent: true,
    })
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.src = item.image
    img.onload = () => {
      texture.image = img
      this.program.uniforms.uImageSizes.value = [img.naturalWidth, img.naturalHeight]
    }
    this.plane = new Mesh(gl, { geometry, program: this.program })
    this.plane.setParent(scene)
    this.addLabel(textColour, font)
    this.resize(screen, viewport)
  }

  private addLabel(colour: string, font: string) {
    // The CMS alt text is a full description for screen readers, far too long to
    // sit under a card, so an empty label just means no label mesh.
    if (!this.item.text) return
    const { texture, width, height } = labelTexture(this.gl, this.item.text, font, colour)
    const program = new Program(this.gl, {
      vertex: `attribute vec3 position; attribute vec2 uv; uniform mat4 modelViewMatrix;
        uniform mat4 projectionMatrix; varying vec2 vUv;
        void main(){ vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
      fragment: `precision mediump float; uniform sampler2D tMap; varying vec2 vUv;
        void main(){ vec4 c = texture2D(tMap, vUv); if (c.a < 0.1) discard; gl_FragColor = c; }`,
      uniforms: { tMap: { value: texture } },
      transparent: true,
    })
    const mesh = new Mesh(this.gl, { geometry: new Plane(this.gl), program })
    const h = this.plane.scale.y * 0.14
    mesh.scale.set(h * (width / height), h, 1)
    mesh.position.y = -this.plane.scale.y * 0.5 - h * 0.6
    mesh.setParent(this.plane)
  }

  update(scroll: { current: number; last: number }, direction: 'left' | 'right') {
    this.plane.position.x = this.x - scroll.current - this.extra
    const x = this.plane.position.x
    const H = this.viewport.width / 2

    if (this.bend === 0) {
      this.plane.position.y = 0
      this.plane.rotation.z = 0
    } else {
      const b = Math.abs(this.bend)
      const R = (H * H + b * b) / (2 * b)
      const ex = Math.min(Math.abs(x), H)
      const arc = R - Math.sqrt(R * R - ex * ex)
      // The arc only ever swings one way from zero, so without this the whole
      // row hangs off the bottom of the band with dead space above it. Shifting
      // by half the deepest possible arc centres the curve in its container.
      const arcMax = R - Math.sqrt(Math.max(0, R * R - H * H))
      const centred = arc - arcMax / 2
      this.plane.position.y = this.bend > 0 ? -centred : centred
      this.plane.rotation.z = (this.bend > 0 ? -1 : 1) * Math.sign(x) * Math.asin(ex / R)
    }

    // Swell toward the middle and settle back on the way out, so whatever is
    // centred reads as the one you are looking at. Scaling both axes by the same
    // factor leaves the plane's aspect alone, so uPlaneSizes stays correct.
    const focus = 1 - Math.min(Math.abs(x) / H, 1)
    const swell = 1 + 0.26 * focus * focus
    this.plane.scale.set(this.baseScaleX * swell, this.baseScaleY * swell, 1)

    this.speed = scroll.current - scroll.last
    this.program.uniforms.uTime.value += 0.04
    this.program.uniforms.uSpeed.value = this.speed

    const half = this.plane.scale.x / 2
    const edge = this.viewport.width / 2
    if (direction === 'right' && this.plane.position.x + half < -edge) this.extra -= this.widthTotal
    if (direction === 'left' && this.plane.position.x - half > edge) this.extra += this.widthTotal
  }

  resize(screen: { width: number; height: number }, viewport: { width: number; height: number }) {
    this.screen = screen
    this.viewport = viewport
    // Cards are sized off container height so they scale with the section
    // rather than the window. The multipliers fill the band; the upstream
    // defaults left a lot of dead space above the arc.
    this.scale = this.screen.height / 1200
    this.baseScaleY = (this.viewport.height * (720 * this.scale)) / this.screen.height
    this.baseScaleX = (this.viewport.width * (560 * this.scale)) / this.screen.width
    this.plane.scale.set(this.baseScaleX, this.baseScaleY, 1)
    this.program.uniforms.uPlaneSizes.value = [this.baseScaleX, this.baseScaleY]
    this.width = this.baseScaleX + 2
    this.widthTotal = this.width * this.length
    // Centre the whole strip on the viewport. Starting the row at x=0 put
    // card 0 dead centre with nothing laid out to its left, so the gallery
    // always loaded with a blank left half until the drift recycled a card.
    this.x = this.width * this.index - (this.widthTotal - this.width) / 2
  }
}

export function RockGallery({
  items,
  bend = 3,
  textColour = '#ffffff',
  font = 'bold 30px Barlow, system-ui, sans-serif',
}: RockGalleryProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const container = containerRef.current
    if (!container || items.length === 0) return

    // A WebGL context is never guaranteed: old hardware, a lost context, or a
    // browser that has blocked it. Bail to the plain grid rather than leaving a
    // section of empty white where the work should be.
    let renderer: Renderer
    try {
      renderer = new Renderer({
        alpha: true,
        antialias: true,
        dpr: Math.min(devicePixelRatio, 2),
      })
      if (!renderer.gl) throw new Error('no webgl context')
    } catch {
      setFailed(true)
      return
    }
    const gl = renderer.gl
    gl.clearColor(0, 0, 0, 0)
    container.appendChild(gl.canvas)
    const onLost = (e: Event) => {
      e.preventDefault()
      setFailed(true)
    }
    gl.canvas.addEventListener('webglcontextlost', onLost)

    const camera = new Camera(gl)
    camera.fov = 45
    camera.position.z = 20
    const scene = new Transform()
    const geometry = new Plane(gl, { heightSegments: 50, widthSegments: 100 })

    let screen = { width: container.clientWidth, height: container.clientHeight }
    let viewport = { width: 0, height: 0 }
    const measure = () => {
      screen = { width: container.clientWidth, height: container.clientHeight }
      renderer.setSize(screen.width, screen.height)
      camera.perspective({ aspect: screen.width / screen.height })
      const h = 2 * Math.tan((camera.fov * Math.PI) / 180 / 2) * camera.position.z
      viewport = { width: h * camera.aspect, height: h }
    }
    measure()

    // Doubled so the loop never shows a gap on a wide viewport.
    const loop = [...items, ...items]
    const cards = loop.map(
      (item, i) =>
        new Card(
          gl,
          geometry,
          scene,
          item,
          i,
          loop.length,
          screen,
          viewport,
          bend,
          textColour,
          font
        )
    )

    const scroll = { current: 0, target: 0, last: 0, ease: 0.05 }
    let down = false
    let start = 0
    let position = 0

    const clientX = (e: MouseEvent | TouchEvent) =>
      'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX
    const onDown = (e: MouseEvent | TouchEvent) => {
      down = true
      position = scroll.current
      start = clientX(e)
    }
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!down) return
      scroll.target = position + (start - clientX(e)) * 0.05
    }
    const onUp = () => {
      down = false
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        scroll.target += 4
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        scroll.target -= 4
      }
    }
    const onResize = () => {
      measure()
      cards.forEach((c) => c.resize(screen, viewport))
    }

    container.addEventListener('mousedown', onDown)
    container.addEventListener('touchstart', onDown, { passive: true })
    window.addEventListener('mousemove', onMove)
    window.addEventListener('touchmove', onMove, { passive: true })
    window.addEventListener('mouseup', onUp)
    window.addEventListener('touchend', onUp)
    container.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)

    // Drift only when the visitor has not asked for less motion, and only while
    // the gallery is actually on screen, so it never burns GPU off-screen.
    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    let visible = false
    const io = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting
    })
    io.observe(container)

    let raf = 0
    const frame = () => {
      if (visible) {
        if (!reduced && !down) scroll.target += 0.012
        scroll.current = lerp(scroll.current, scroll.target, scroll.ease)
        const direction = scroll.current > scroll.last ? 'right' : 'left'
        cards.forEach((c) => c.update(scroll, direction))
        renderer.render({ scene, camera })
        scroll.last = scroll.current
      }
      raf = requestAnimationFrame(frame)
    }
    frame()

    return () => {
      cancelAnimationFrame(raf)
      io.disconnect()
      container.removeEventListener('mousedown', onDown)
      container.removeEventListener('touchstart', onDown)
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('mouseup', onUp)
      window.removeEventListener('touchend', onUp)
      container.removeEventListener('keydown', onKey)
      window.removeEventListener('resize', onResize)
      gl.canvas.removeEventListener('webglcontextlost', onLost)
      gl.canvas.parentNode?.removeChild(gl.canvas)
    }
  }, [items, bend, textColour, font])

  if (failed) {
    return (
      <div className="grid h-full grid-cols-2 gap-3 sm:grid-cols-3">
        {items.slice(0, 6).map((item) => (
          <div
            key={item.image}
            className="border-brand-purple/20 relative overflow-hidden border-4"
          >
            {/* eslint-disable-next-line @next/next/no-img-element -- the canvas
                path already loaded these; a plain img keeps the fallback simple. */}
            <img src={item.image} alt="" className="h-full w-full object-cover" />
          </div>
        ))}
      </div>
    )
  }

  return (
    <div
      ref={containerRef}
      tabIndex={0}
      role="region"
      aria-label="Rock wall gallery. Drag, or use the left and right arrow keys."
      className="h-full w-full cursor-grab overflow-hidden focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white active:cursor-grabbing"
    />
  )
}
